/**
 * /errors - the cross-tenant inbox of escalated tenant errors (OCP-557).
 *
 * One row per (tenant, fingerprint): what broke, where, how often, on how
 * many tenants, which orboto version. Resolve marks a row handled (a later
 * report re-escalates); "Create ticket" opens the workspace ticket through
 * the feedback intake settings. Same endpoints as the MCP tools
 * ocp_list_tenant_errors / ocp_get_tenant_error / ocp_resolve_tenant_error /
 * ocp_create_tenant_error_ticket.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ApiError, api } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

type Severity = 'warning' | 'error' | 'fatal';
type State = 'open' | 'resolved' | 'all';

interface ErrorRow {
  id: string;
  tenant_id: string;
  tenant_subdomain: string | null;
  instance_id: string;
  fingerprint: string;
  title: string;
  culprit: string | null;
  route: string | null;
  status: number | null;
  severity: Severity;
  count: number;
  reports: number;
  first_seen: string;
  last_seen: string;
  version: string | null;
  trigger: string | null;
  first_received_at: string;
  last_received_at: string;
  notified_at: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  operator_ticket_key: string | null;
  last_ticket_error: string | null;
  tenants_with_fingerprint: number;
}
interface ErrorDetail extends ErrorRow {
  stack: string | null;
}
interface ListResponse {
  items: ErrorRow[];
  total: number;
  counts: { open: number; resolved: number; by_severity: Record<string, number> };
}

const SEVERITY_CLASS: Record<Severity, string> = {
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  error: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  fatal: 'bg-red-600 text-white',
};

export function AdminTenantErrorsPage() {
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState<State>('open');
  const [severity, setSeverity] = useState<'' | Severity>('');
  const [q, setQ] = useState('');
  const [data, setData] = useState<ListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(params.get('id'));
  const [detail, setDetail] = useState<ErrorDetail | null>(null);
  const [busy, setBusy] = useState<'' | 'resolve' | 'ticket'>('');
  const [note, setNote] = useState<string | null>(null);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    p.set('state', state);
    if (severity) p.set('severity', severity);
    if (q.trim()) p.set('q', q.trim());
    p.set('limit', '200');
    return `?${p.toString()}`;
  }, [state, severity, q]);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api<ListResponse>(`/v1/admin/tenant-errors${queryString}`));
    } catch (err) {
      setError(err instanceof ApiError ? `Could not load tenant errors (HTTP ${err.status}).` : 'Could not load tenant errors.');
    }
  }, [queryString]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!openId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const d = await api<ErrorDetail>(`/v1/admin/tenant-errors/${openId}`);
        if (!cancelled) setDetail(d);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? `Could not load the error (HTTP ${err.status}).` : 'Could not load the error.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openId]);

  function open(id: string | null) {
    setOpenId(id);
    setNote(null);
    const next = new URLSearchParams(params);
    if (id) next.set('id', id);
    else next.delete('id');
    setParams(next, { replace: true });
  }

  async function resolve(id: string) {
    setBusy('resolve');
    setNote(null);
    try {
      const d = await api<ErrorDetail>(`/v1/admin/tenant-errors/${id}/resolve`, { method: 'POST' });
      setDetail(d);
      setNote('Marked as handled. A new report of the same error re-escalates.');
      await load();
    } catch (err) {
      setNote(err instanceof ApiError ? `Could not resolve (HTTP ${err.status}).` : 'Could not resolve.');
    } finally {
      setBusy('');
    }
  }

  async function createTicket(id: string) {
    setBusy('ticket');
    setNote(null);
    try {
      const r = await api<{ result: string; ticketKey: string | null }>(`/v1/admin/tenant-errors/${id}/ticket`, { method: 'POST' });
      setNote(r.result === 'already_created' ? `Ticket exists: ${r.ticketKey}` : `Ticket ${r.ticketKey} created.`);
      setDetail(await api<ErrorDetail>(`/v1/admin/tenant-errors/${id}`));
      await load();
    } catch (err) {
      const body = err instanceof ApiError ? ((err.body as { error?: string; error_detail?: string } | null) ?? {}) : {};
      setNote(
        body.error === 'intake_not_configured'
          ? 'The feedback intake (workspace API) is not configured - set feedback.intake_api_url / _api_key / _project_key.'
          : `Could not create the ticket${body.error ? ` (${body.error})` : ''}.`,
      );
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Tenant errors</h1>
          <p className="text-sm text-gray-500">
            Errors tenant instances escalated to the control plane - signature only, no customer data. One row per tenant and fingerprint; the same fingerprint on several tenants is a core bug.
          </p>
        </div>
        {data && (
          <div className="flex gap-2 text-xs" data-testid="tenant-errors-counts">
            <span className="rounded bg-gray-100 dark:bg-gray-800 px-2 py-1">open: {data.counts.open}</span>
            <span className="rounded bg-gray-100 dark:bg-gray-800 px-2 py-1">resolved: {data.counts.resolved}</span>
            {(['fatal', 'error', 'warning'] as Severity[]).map((s) => (
              <span key={s} className={`rounded px-2 py-1 ${SEVERITY_CLASS[s]}`}>
                {s}: {data.counts.by_severity[s] ?? 0}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-xs text-gray-500">State</span>
          <select aria-label="State" value={state} onChange={(e) => setState(e.target.value as State)} className="rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1 text-sm">
            <option value="open">open</option>
            <option value="resolved">resolved</option>
            <option value="all">all</option>
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Severity</span>
          <select aria-label="Severity" value={severity} onChange={(e) => setSeverity(e.target.value as '' | Severity)} className="rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1 text-sm">
            <option value="">all</option>
            <option value="fatal">fatal</option>
            <option value="error">error</option>
            <option value="warning">warning</option>
          </select>
        </label>
        <input aria-label="Search" type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="title / culprit / route" className="w-64 rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1 text-sm" />
      </div>

      {error && (
        <div role="alert" className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Severity</th>
                <th className="px-3 py-2 text-left">Error</th>
                <th className="px-3 py-2 text-left">Tenant</th>
                <th className="px-3 py-2 text-right">Count</th>
                <th className="px-3 py-2 text-left">Last seen</th>
                <th className="px-3 py-2 text-left">Ticket</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {data?.items.map((r) => (
                <tr key={r.id} onClick={() => open(r.id)} data-testid={`tenant-error-row-${r.id}`} className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${openId === r.id ? 'bg-brand-50/40 dark:bg-gray-800' : ''}`}>
                  <td className="px-3 py-2">
                    <span className={`rounded px-2 py-0.5 text-xs ${SEVERITY_CLASS[r.severity]}`}>{r.severity}</span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{r.title}</div>
                    <div className="text-xs text-gray-500">
                      {r.route ?? r.culprit ?? '-'}
                      {r.status ? ` · HTTP ${r.status}` : ''}
                      {r.version ? ` · ${r.version}` : ''}
                      {r.tenants_with_fingerprint > 1 ? ` · on ${r.tenants_with_fingerprint} tenants` : ''}
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{r.tenant_subdomain ?? r.tenant_id.slice(0, 8)}</td>
                  <td className="px-3 py-2 text-right">{r.count}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDateTime(r.last_seen)}</td>
                  <td className="px-3 py-2 text-xs">{r.operator_ticket_key ?? (r.resolved_at ? 'resolved' : '-')}</td>
                </tr>
              ))}
              {data && data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-center text-sm text-gray-500">
                    No escalated errors{state === 'open' ? ' open' : ''}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div>
          {detail ? (
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 text-sm" data-testid="tenant-error-detail">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className={`rounded px-2 py-0.5 text-xs ${SEVERITY_CLASS[detail.severity]}`}>{detail.severity}</span>
                  <h2 className="mt-1 font-semibold text-gray-900 dark:text-gray-100">{detail.title}</h2>
                </div>
                <button type="button" onClick={() => open(null)} className="text-xs text-gray-500 hover:underline">
                  close
                </button>
              </div>
              <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                <dt className="text-gray-500">Tenant</dt>
                <dd className="font-mono">{detail.tenant_subdomain ?? detail.tenant_id} · instance {detail.instance_id.slice(0, 8)}</dd>
                <dt className="text-gray-500">Fingerprint</dt>
                <dd className="font-mono">{detail.fingerprint} · on {detail.tenants_with_fingerprint} tenant{detail.tenants_with_fingerprint === 1 ? '' : 's'}</dd>
                <dt className="text-gray-500">Route</dt>
                <dd className="font-mono">{detail.route ?? '-'}{detail.status ? ` (HTTP ${detail.status})` : ''}</dd>
                <dt className="text-gray-500">Culprit</dt>
                <dd className="font-mono">{detail.culprit ?? '-'}</dd>
                <dt className="text-gray-500">Occurrences</dt>
                <dd>{detail.count} since {fmtDateTime(detail.first_seen)}, last {fmtDateTime(detail.last_seen)} ({detail.reports} report{detail.reports === 1 ? '' : 's'}, {detail.trigger ?? '-'})</dd>
                <dt className="text-gray-500">Version</dt>
                <dd>{detail.version ?? '-'}</dd>
                <dt className="text-gray-500">Notified</dt>
                <dd>{detail.notified_at ? fmtDateTime(detail.notified_at) : 'not yet'}</dd>
                <dt className="text-gray-500">Resolved</dt>
                <dd>{detail.resolved_at ? `${fmtDateTime(detail.resolved_at)} by ${detail.resolved_by ?? '-'}` : 'open'}</dd>
                <dt className="text-gray-500">Ticket</dt>
                <dd>{detail.operator_ticket_key ?? '-'}{detail.last_ticket_error ? ` · last error: ${detail.last_ticket_error}` : ''}</dd>
              </dl>
              {detail.stack && (
                <pre className="mt-3 max-h-80 overflow-auto rounded bg-gray-50 dark:bg-gray-800 p-2 text-[11px] leading-snug" data-testid="tenant-error-stack">
                  {detail.stack}
                </pre>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {!detail.resolved_at && (
                  <button type="button" onClick={() => void resolve(detail.id)} disabled={busy !== ''} data-testid="tenant-error-resolve" className="rounded bg-emerald-600 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
                    {busy === 'resolve' ? 'Resolving...' : 'Mark handled'}
                  </button>
                )}
                {!detail.operator_ticket_key && (
                  <button type="button" onClick={() => void createTicket(detail.id)} disabled={busy !== ''} data-testid="tenant-error-ticket" className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50">
                    {busy === 'ticket' ? 'Creating...' : 'Create ticket'}
                  </button>
                )}
              </div>
              {note && <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">{note}</p>}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-4 text-sm text-gray-500">Select a row to see the stack and act on it.</div>
          )}
        </div>
      </div>
    </div>
  );
}
